import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Facebook, Twitter, Linkedin, Instagram } from 'lucide-react';
import { useConference } from '@/context/ConferenceContext';
import Logo from './Logo';


const Footer = () => {
  const {
    getConferenceName,
    getConferenceTheme,
    getContact,
    getSocial,
    getDates,
    getImportantDates,
    loginDetails,
    loading,
    importantDetails
  } = useConference();

  const contact = getContact();
  const social = getSocial();
  const dates = getDates();
  const importantDates = getImportantDates();

  if (loading) {
    return (
      <footer className="bg-gray-50 border-t" data-testid="main-footer">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="space-y-3">
                <div className="h-6 w-32 bg-gray-700 rounded animate-pulse"></div>
                <div className="space-y-2">
                  {[...Array(3)].map((_, j) => (
                    <div key={j} className="h-4 w-full bg-gray-700 rounded animate-pulse"></div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </footer>
    );
  }

  return (
    <footer className="bg-gray-50 border-t text-gray-900" data-testid="main-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Conference Info */}
          <div>
            <h2 className="text-2xl font-bold mb-2 text-gray-900">
              {getConferenceName().includes('<') ? (
                <span dangerouslySetInnerHTML={{ __html: getConferenceName() }} />
              ) : (
                getConferenceName()
              )}
            </h2>
            <p className="mb-4 text-sm text-gray-600">
              {getConferenceTheme() || 'Empowering professionals for the future through innovation and leadership.'}
            </p>
            {(loginDetails?.email || contact.email1) && (
              <div className="flex items-center gap-2 text-sm mb-1 text-gray-600">
                <Mail size={16} />
                <a href={`mailto:${loginDetails?.email || contact.email1}`} className="hover:text-sky-500">{loginDetails?.email || contact.email1}</a>
              </div>
            )}
            {contact.phone && (
              <div className="flex items-center gap-2 text-sm mb-1 text-gray-600">
                <Phone size={16} />
                <a href={`tel:${contact.phone}`} className="hover:text-sky-500">{contact.phone}</a>
              </div>
            )}
            {contact.pcName && (
              <div className="flex items-center gap-2 text-sm mb-1 text-gray-600">
                <MapPin size={16} />
                <span>{contact.pcName}</span>
              </div>
            )}
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="font-semibold text-gray-900 mb-4">Quick Links</h3>
            <ul className="space-y-2">
              <li><Link to="/register" className="hover:text-sky-500">Registration</Link></li>
              <li><Link to="/submission" className="hover:text-sky-500">Submit Abstract</Link></li>
              <li><Link to="/schedule" className="hover:text-sky-500">Agenda</Link></li>
              <li><Link to="/speakers" className="hover:text-sky-500">Speakers</Link></li>
            </ul>
          </div>

          {/* Important Dates */}
          <div>
            <h3 className="font-semibold text-gray-900 mb-4">Important Dates</h3>
            <ul className="space-y-2 text-sm text-gray-600">
              {importantDates.registration_opens && (
                <li className="flex items-center gap-2"><span role="img" aria-label="calendar">📅</span> Registration Opens: {importantDates.registration_opens}</li>
              )}
              {importantDates.EarlyBird && (
                <li className="flex items-center gap-2"><span role="img" aria-label="calendar">📅</span> Early Bird: {importantDates.EarlyBird}</li>
              )}
              {importantDates.abstract_submission_deadline && (
                <li className="flex items-center gap-2"><span role="img" aria-label="calendar">📅</span> Abstract Deadline: {importantDates.abstract_submission_deadline}</li>
              )}
              {importantDates.mid_term && (
                <li className="flex items-center gap-2"><span role="img" aria-label="calendar">📅</span> Mid Term: {importantDates.mid_term}</li>
              )}
              {importantDates.OnSpot && (
                <li className="flex items-center gap-2"><span role="img" aria-label="calendar">📅</span> On Spot: {importantDates.OnSpot}</li>
              )}
              {importantDetails?.ConferenceDates && (
                <li className="flex items-center gap-2"><span role="img" aria-label="calendar">📅</span> Conference: {importantDetails.ConferenceDates}</li>
              )}
            </ul>
          </div>

          {/* Stay Connected */}
          <div>
            <h3 className="font-semibold text-gray-900 mb-4">Stay Connected</h3>
            <div className="flex gap-4 mt-2">
              <a
                href={social.facebook || undefined}
                target="_blank"
                rel="noopener noreferrer"
                className={`hover:text-sky-500 ${!social.facebook ? 'opacity-40 pointer-events-none' : ''}`}
                aria-label="Facebook"
              >
                <Facebook size={20} />
              </a>
              <a
                href={social.twitter || undefined}
                target="_blank"
                rel="noopener noreferrer"
                className={`hover:text-sky-500 ${!social.twitter ? 'opacity-40 pointer-events-none' : ''}`}
                aria-label="Twitter"
              >
                <Twitter size={20} />
              </a>
              <a
                href={social.instagram || undefined}
                target="_blank"
                rel="noopener noreferrer"
                className={`hover:text-sky-500 ${!social.instagram ? 'opacity-40 pointer-events-none' : ''}`}
                aria-label="Instagram"
              >
                <Instagram size={20} />
              </a>
              <a
                href={social.linkedin || undefined}
                target="_blank"
                rel="noopener noreferrer"
                className={`hover:text-sky-500 ${!social.linkedin ? 'opacity-40 pointer-events-none' : ''}`}
                aria-label="LinkedIn"
              >
                <Linkedin size={20} />
              </a>
            </div>
          </div>
        </div>

        <div className="mt-12 pt-8 border-t text-center text-sm text-gray-600">
          <p>© 2026 CCAI-2026 . All rights reserved.</p>
        </div>
      </div>
    </footer>
  );
};

function formatDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
}

function formatDateRange(startStr, endStr) {
  if (!startStr || !endStr) return '';
  const start = new Date(startStr);
  const end = new Date(endStr);
  const options = { year: 'numeric', month: 'long', day: 'numeric' };
  return `${start.toLocaleDateString('en-US', options)} - ${end.toLocaleDateString('en-US', options)}`;
}

export default Footer;
